import { useLayoutEffect, useState, forwardRef } from 'react';
import type { FC, KeyboardEvent, PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import Flex from '../../Flex';
import { defaultThemeProp } from '../../../theme';
import Text from '../../Text';
import { cap } from '../../../utils';
import { getDateOfWeek, getWeek, parseToDate, toUTCDateString } from '../Input/utils';
import { useConfiguration, useConsolidatedRef, useFocusWithin } from '../../../hooks';
import { CALENDAR_TYPES, type CalendarType } from '../Input/local';
import type { ForwardProps } from '../../../types';
import type { FormControlProps } from '../../FormControl';

import { StyledColumnLabel, StyledPick } from './Calendar.styles';
import { getWeeksOfMonth } from './utils';

export interface WeeksProps {
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: FormControlProps['id'];
  /** Defines default week that will be selected when the Date Picker is opened. */
  selected?: Date;
  /**
   * Defines default week that will be focused initially when the Date Picker is opened.
   * @default new Date()
   */
  current?: Date;
  /** Callback fired when user focuses new week. */
  onFocus?: (date: Date) => void;
  /** Callback fired when new week is selected. */
  onSelect?: (date: Date) => void;
  /** Sets the type of calendar to be used. This will be determined by locale if no value is provided. */
  calendarType?: CalendarType;
  /** Defines minimum available value that can be selected by the user. */
  min?: string | number | Date;
  /** Defines maximum available value that can be selected by the user. */
  max?: string | number | Date;
  /** Set the calendar to disabled state, ie. non-interactive. */
  disabled?: boolean;
  /** Should text be in long or short format (ie. only number). */
  size?: 'short' | 'long';
}

export const StyledWeekNumbers = styled.div`
  margin-inline-end: ${props => props.theme.base.spacing};
  color: rgba(0, 0, 0, 0.75);
`;

StyledWeekNumbers.defaultProps = defaultThemeProp;

const StyledWeekNumber = styled(StyledPick)(props => {
  const {
    theme: {
      base: { 'border-radius': borderRadius }
    }
  } = props;

  return css`
    &:disabled {
      &:nth-child(2) {
        border-radius: calc(0.5 * ${borderRadius}) calc(0.5 * ${borderRadius}) 0 0;
      }

      &:last-child {
        border-radius: 0 0 calc(0.5 * ${borderRadius}) calc(0.5 * ${borderRadius});
      }
    }
  `;
});

StyledWeekNumber.defaultProps = defaultThemeProp;

const Weeks: FC<WeeksProps & ForwardProps> = forwardRef(function Weeks(
  props: PropsWithoutRef<WeeksProps>,
  ref: Ref<HTMLButtonElement>
) {
  const {
    selected,
    current = selected ? new Date(selected.getTime()) : new Date(),
    calendarType = CALENDAR_TYPES.ISO_8601,
    onSelect,
    onFocus,
    disabled = false,
    size = 'short'
  } = props;

  const { locale } = useConfiguration();

  const [focusedWeek, setFocusedWeek] = useState(current);
  useLayoutEffect(() => {
    setFocusedWeek(current);
  }, [current.getUTCFullYear(), current.getUTCMonth()]);

  let newFocused: Date;
  const onKeyDown = (ev: KeyboardEvent<HTMLElement>) => {
    if (['ArrowUp', 'ArrowDown', 'Enter'].includes(ev.key)) ev.preventDefault();
    switch (ev.key) {
      case 'ArrowUp':
        newFocused = new Date(focusedWeek);
        newFocused.setUTCDate(focusedWeek.getUTCDate() - 7);
        setFocusedWeek(newFocused);
        onFocus?.(newFocused);
        break;
      case 'ArrowDown':
        newFocused = new Date(focusedWeek);
        newFocused.setUTCDate(focusedWeek.getUTCDate() + 7);
        setFocusedWeek(newFocused);
        onFocus?.(newFocused);
        break;
      case 'Enter':
        onSelect?.(focusedWeek);
        break;
      default:
    }
  };

  const containerRef = useConsolidatedRef(ref);
  const hasFocus = useFocusWithin([containerRef]);

  const min = props.min ? parseToDate(props.min) : -Infinity;
  const max = props.max ? parseToDate(props.max) : Infinity;
  const isDisabled = (day: Date) => {
    return disabled || Number(min) > day.getTime() || day.getTime() > Number(max);
  };

  const isChecked = (day: Date) => {
    return (
      selected &&
      toUTCDateString(day) ===
        toUTCDateString(
          getDateOfWeek(
            getWeek(selected, calendarType, 'UTC'),
            selected.getFullYear(),
            calendarType
          )
        )
    );
  };

  const isFocused = (day: Date) => {
    return (
      toUTCDateString(day) ===
      toUTCDateString(
        getDateOfWeek(
          getWeek(focusedWeek, calendarType, 'UTC'),
          focusedWeek.getFullYear(),
          calendarType
        )
      )
    );
  };

  const displayNames = new Intl.DisplayNames(locale, {
    style: 'long',
    type: 'dateTimeField'
  }) as Intl.DateTimeDisplayNames;
  const displayNamesShort = new Intl.DisplayNames(locale, {
    style: 'short',
    type: 'dateTimeField'
  }) as Intl.DateTimeDisplayNames;

  const weeks = getWeeksOfMonth(current, calendarType).map(({ weekNo, weekYear }) => {
    const dateOfWeek = getDateOfWeek(weekNo, weekYear, calendarType);
    return (
      <StyledWeekNumber
        key={weekNo}
        variant='simple'
        icon={false}
        type='button'
        loading={false}
        ref={
          isFocused(dateOfWeek)
            ? (focusedRef: HTMLButtonElement) => {
                if (hasFocus) focusedRef?.focus();
              }
            : undefined
        }
        tabIndex={isFocused(dateOfWeek) ? 0 : -1}
        disabled={isDisabled(dateOfWeek)}
        aria-selected={isChecked(dateOfWeek) || undefined}
        aria-label={`${displayNames.of('weekOfYear')} ${weekNo.toString()} ${weekYear.toString()}`}
        onClick={() => {
          onSelect?.(dateOfWeek);
        }}
      >
        {size === 'long' && `${cap(displayNames.of('weekOfYear'))} `}
        {weekNo.toString()}
      </StyledWeekNumber>
    );
  });

  const label =
    size === 'short' ? (
      <Text as={StyledColumnLabel} forwardedAs='abbr' title={cap(displayNames.of('weekOfYear'))}>
        {cap(displayNamesShort.of('weekOfYear'))}
      </Text>
    ) : (
      <Text as={StyledColumnLabel}>{cap(displayNames.of('weekOfYear'))}</Text>
    );

  return (
    <Flex
      as={StyledWeekNumbers}
      container={{ direction: 'column' }}
      ref={containerRef}
      onKeyDown={onKeyDown}
    >
      {label}
      {weeks}
    </Flex>
  );
});

export default Weeks;
